import apiClient, {
  api,
  persistNativeTokensFromPayload,
} from '@/integrations/clients/carAuction/carAuctionClient';
import {
  clearNativeTokens,
  isNativeCapacitorApp,
} from '@/integrations/clients/carAuction/nativeAuth';
import { Capacitor } from '@capacitor/core';
import { User, UserType } from '@/types/user';
import { mapApiUserTypeToUserType } from '@/utils/authUtils';

// Toggle to use mocked auth flows (no backend required)
const USE_MOCK_AUTH = false;

function nativeBankIdClientFields(): {
  nativeClient?: true;
  nativePlatform?: string;
} {
  if (!isNativeCapacitorApp()) return {};
  return {
    nativeClient: true,
    nativePlatform: Capacitor.getPlatform(),
  };
}

// Backend response types
interface AdminLoginResponse {
  success: boolean;
  actor: 'admin';
  user: {
    id: string;
    username?: string;
    firstName?: string;
    lastName?: string;
    email: string;
    emailVerified?: boolean;
    phone?: string;
  };
  permissions: any;
}

interface UserLoginResponse {
  success: boolean;
  actor: 'user';
  data: {
    id: string;
    name: string; // Backend returns username as "name"
    email: string;
    type: string;
    emailVerified?: boolean;
    accountTermsAccepted?: boolean;
    otherTermsAccepted?: boolean;
    phone?: string;
    companyId?: string | null;
  };
}

export interface RegisterData {
  personalInfo: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
    type: 'dealer' | 'consumer' | 'business';
    personalNumber?: string;
    phone: string;
  };
  message?: string;
  // Company info (for dealer/business)
  companyId?: string; // Reference to Company object
  /** Required for consumer (INDIVIDUAL) registration after email OTP */
  otpProof?: string;
}

export interface AuthResponse {
  user: User;
  token: string;
}

export interface LoginResponse {
  user: User;
  companyId?: string | null;
}

export class AuthService {
  // Register new user
  static async register(userData: RegisterData): Promise<void> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 500));
        return;
      }
      await api.post<void>('/users/register', userData);
  }

  static async startBankIdLogin(
    redirect?: string
  ): Promise<{
    success: boolean;
    redirectUrl: string;
    continueUrl?: string;
    nativeClient?: boolean;
  }> {
    return api.post<{
      success: boolean;
      redirectUrl: string;
      continueUrl?: string;
      nativeClient?: boolean;
    }>('/auth/bankid/login', {
      redirect,
      ...nativeBankIdClientFields(),
    });
  }

  static async exchangeNativeBankIdCode(code: string): Promise<{
    success: boolean;
    flow: 'login' | 'register';
    nativeTokens: { accessToken: string; refreshToken: string };
  }> {
    return api.post<{
      success: boolean;
      flow: 'login' | 'register';
      nativeTokens: { accessToken: string; refreshToken: string };
    }>('/auth/bankid/native-exchange', { code });
  }

  static async sendEmailOtp(
    email: string,
    purpose: 'register' | 'verify_account' = 'register',
  ): Promise<{
    success: boolean;
    expiresAt?: string;
    expiresInSeconds?: number;
    purpose?: string;
  }> {
    return api.post<{
      success: boolean;
      expiresAt?: string;
      expiresInSeconds?: number;
      purpose?: string;
    }>('/auth/email-otp/send', { email, purpose });
  }

  static async verifyEmailOtp(
    email: string,
    code: string,
    purpose: 'register' | 'verify_account' = 'register',
  ): Promise<{ success: boolean; otpProof?: string; emailVerified?: boolean }> {
    return api.post<{ success: boolean; otpProof?: string; emailVerified?: boolean }>(
      '/auth/email-otp/verify',
      { email, code, purpose },
    );
  }

  static async verifyEmailOtpAndLogin(
    email: string,
    code: string,
    password: string,
  ): Promise<LoginResponse> {
    const data = await api.post<UserLoginResponse>('/auth/email-otp/verify-login', {
      email,
      code,
      password,
    });
    persistNativeTokensFromPayload(data);

    const nameParts = (data.data.name || '').trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    return {
      user: {
        id: data.data.id,
        firstName,
        lastName,
        email: data.data.email,
        type: mapApiUserTypeToUserType(data.data.type),
        phone: data.data.phone ?? undefined,
        emailVerified: true,
        accountTermsAccepted: data.data.accountTermsAccepted ?? false,
        otherTermsAccepted: data.data.otherTermsAccepted ?? false,
      },
      companyId: data.data.companyId ?? null,
    };
  }

  static async startBankIdRegister(payload: {
    username?: string;
    email: string;
    phoneNumber: string;
    password: string;
    type: 'dealer' | 'consumer' | 'business';
    companyId?: string;
    message?: string;
    otpProof: string;
  }): Promise<{
    success: boolean;
    redirectUrl: string;
    continueUrl?: string;
    nativeClient?: boolean;
  }> {
    return api.post<{
      success: boolean;
      redirectUrl: string;
      continueUrl?: string;
      nativeClient?: boolean;
    }>('/auth/bankid/register/start', {
      ...payload,
      ...nativeBankIdClientFields(),
    });
  }

  // Login user (cookie-based)
  static async login(credentials: { email: string; password: string; isTest: boolean; emailVerified?: boolean }): Promise<LoginResponse> {
      if (!credentials.isTest && !USE_MOCK_AUTH) {
        const response = await apiClient.post<AdminLoginResponse | UserLoginResponse>(
          '/auth/login',
          { email: credentials.email, password: credentials.password },
        );
        const data = response.data;
        persistNativeTokensFromPayload(data);
        
        if (data.actor === 'admin') {
          // Admin response
          const adminData = data as AdminLoginResponse;
          const username = adminData.user.username?.trim() || '';
          const usernameParts = username.split(/\s+/);
          return {
            user: {
              id: adminData.user.id, // Backend returns string (cuid)
              firstName:
                adminData.user.firstName ||
                usernameParts[0] ||
                username ||
                '',
              lastName:
                adminData.user.lastName ||
                usernameParts.slice(1).join(' ') ||
                '',
              email: adminData.user.email,
              type: UserType.Admin,
              phone: adminData.user.phone ?? undefined,
              emailVerified: adminData.user.emailVerified ?? true,
            },
            companyId: null, // Admin users don't have companies
          };
        } else {
          // User response
          const userData = data as UserLoginResponse;
          // Backend returns username as "name", split it into firstName and lastName
          const nameParts = (userData.data.name || '').trim().split(/\s+/);
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          return {
            user: {
              id: userData.data.id, // Backend now returns id
              firstName,
              lastName,
              email: userData.data.email,
              type: mapApiUserTypeToUserType(userData.data.type),
              phone: userData.data.phone ?? undefined,
              emailVerified: userData.data.emailVerified ?? true,
              accountTermsAccepted: userData.data.accountTermsAccepted ?? false,
              otherTermsAccepted: userData.data.otherTermsAccepted ?? false,
            },
            companyId: userData.data.companyId ?? null,
          };
        }
      }
      // Mock/Test response
      return {
        user: {
          id: 'test-user',
          firstName: 'Test',
          lastName: 'User',
          email: 'test@example.com',
          type: UserType.Individual,
          emailVerified: true,
        },
        companyId: null,
      };
  }

  // Logout user
  static async logout(): Promise<void> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 200));
        return;
      }
      try {
        await api.post('/auth/logout');
      } finally {
        if (isNativeCapacitorApp()) {
          clearNativeTokens();
        }
      }
  }

  // Request password reset email
  static async requestPasswordReset(email: string): Promise<void> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 500));
        return;
      }
      await api.post<void>('/auth/forgot-password', { email });
  }

  // Resend verification email
  static async resendVerificationEmail(email: string): Promise<void> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 400));
        return;
      }
      await api.post<void>('/auth/resend-verification', { email });
  }

  // Check whether email is verified (by email)
  static async checkEmailVerified(email: string): Promise<boolean> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 300));
        return true;
      }
      const response = await api.post<{ verified: boolean }>('/auth/check-email-verified', { email });
      return !!response.verified;
  }

  // Verify email with token from email link
  static async verifyEmail(token: string): Promise<{ alreadyVerified?: boolean }> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 300));
        return { alreadyVerified: false };
      }
      return await api.post<{ success: boolean; alreadyVerified?: boolean }>('/auth/verify-email', { token });
  }

  // Validate password reset token (check if token is valid before showing form)
  static async validateResetToken(token: string): Promise<boolean> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 200));
        return true;
      }
      const response = await api.get<{ valid: boolean }>('/auth/validate-reset-token', { params: { token } });
      return !!response.valid;
  }

  // Complete password reset with token from email
  static async resetPassword(payload: { token: string; password: string; email?: string }): Promise<void> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 500));
        return;
      }
      await api.post<void>('/auth/reset-password', { token: payload.token, newPassword: payload.password });

  }

  // Update password for logged-in user (Settings)
  static async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 400));
        return;
      }
      await api.put<void>('/users/updatePassword', { currentPassword, newPassword });
  }

  // Delete user account
  static async deleteAccount(reason: string, customReason?: string): Promise<void> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 1000));
        return;
      }
      await api.delete<void>('/auth/account', { 
        data: { 
          reason, 
          customReason: reason === 'other' ? customReason : undefined 
        } 
      });
  }

  // Delete user account (admin function)
  static async deleteUser(payload: { userId: string; reason: string; customReason?: string }): Promise<void> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 1000));
        return;
      }
      await api.delete<void>(`/auth/users/${payload.userId}`, { 
        data: { 
          reason: payload.reason, 
          customReason: payload.customReason 
        } 
      });
  }

  // Accept terms and conditions
  // Requires authentication - user must be logged in
  static async acceptTerms(): Promise<void> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 300));
        return;
      }
      await api.post<void>('/auth/accept-terms', {});
  }

  // Accept other terms (seller, auction, buyer consumer, buyer B2B, pricing)
  // These must be accepted before ID verification can be initiated
  static async acceptOtherTerms(): Promise<void> {
      if (USE_MOCK_AUTH) {
        await new Promise((r) => setTimeout(r, 300));
        return;
      }
      await api.post<void>('/auth/accept-other-terms', {});
  }
}